import type MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import hasFeature from 'modules/core/lib/hasFeature'

export const SHIFT_CONFLICTS = 'shift_conflicts'
export const SHIFT_WITHOUT_CONFLICTS = 'shift_without_conflicts'

export const SHIFT_MANAGEMENT_FILTERS = {
  employeeLocation: 'location',
} as const

export interface CustomFilter {
  field: string
  condition: string
  value: string
}

export interface FilterState {
  custom_filters: CustomFilter[]
  status_id: string | null
  employee_ids: string[] | null
  logic_operator: string | null
  shift_ids: number[] | null
  without_shifts_ids: number[] | null
  terminated: boolean | null
  locationIds: number[]
}

export type FiltersDispatch = (arg0: FiltersAction) => void

export type FiltersAction =
  | {
      type: 'CLEAR_FILTERS'
      newEntryFlowEnabled: boolean
    }
  | {
      type: 'FILTER_CUSTOM'
      id: string | null
      employeeIds: string[] | null
      shiftIds: number[] | null
      withoutShiftsIds: number[] | null
      customFilters: CustomFilter[]
      logicOperator: MultiFilterForm
      terminated: boolean | null
    }
  | {
      type: 'FILTER_EMPLOYEE_IDS'
      employeeIds: string[]
    }
  | {
      type: 'RESET_SHIFT_IDS'
    }
  | {
      type: 'SET_LOCATIONS'
      locationIds: number[]
    }

export function initialFiltersState(): FilterState {
  return {
    custom_filters: [],
    status_id: null,
    employee_ids: null,
    shift_ids: null,
    without_shifts_ids: null,
    logic_operator: null,
    terminated: null,
    locationIds: [],
  }
}

function isEmptyState(filter: FilterState): boolean {
  return (
    !filter.custom_filters.length &&
    !filter.status_id &&
    !filter.employee_ids &&
    !filter.shift_ids &&
    !filter.without_shifts_ids &&
    !filter.logic_operator &&
    !filter.terminated &&
    !filter.locationIds
  )
}

export default function reducer(state: FilterState, action: FiltersAction): FilterState {
  switch (action.type) {
    case 'FILTER_CUSTOM':
      return {
        ...state,
        custom_filters: action.customFilters,
        status_id: action.id,
        employee_ids: action.employeeIds,
        shift_ids: action.shiftIds,
        without_shifts_ids: action.withoutShiftsIds,
        logic_operator: action.logicOperator.get('logic_operator').value,
        terminated: action.terminated,
      }

    case 'FILTER_EMPLOYEE_IDS':
      return {
        ...state,
        custom_filters: [],
        status_id: null,
        employee_ids: action.employeeIds,
        shift_ids: null,
        without_shifts_ids: null,
        logic_operator: null,
        terminated: null,
      }

    case 'CLEAR_FILTERS':
      // When it's GraphQL we should reset the state to refetch the query.
      if (isEmptyState(state) && !hasFeature('dev_shift_management_graphql_migration')) return state

      // Location filtering is now managed by the LocationSelector component which handles filter state management, so we preserve existing locationIds
      if (action.newEntryFlowEnabled) {
        return {
          ...initialFiltersState(),
          locationIds: state.locationIds,
        }
      }
      return initialFiltersState()

    case 'RESET_SHIFT_IDS':
      return {
        ...state,
        shift_ids: null,
        without_shifts_ids: null,
      }

    case 'SET_LOCATIONS':
      return {
        ...state,
        locationIds: action.locationIds,
      }

    default:
      return state
  }
}
